import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { HERO_IMAGE, INTERIOR_IMAGES } from "../data/site";
import { scrollToSection } from "../lib/lenis";
import { RotatingImage } from "./RotatingImage";
import { useLang } from "../i18n/LanguageContext";

const HERO_SLIDES = [HERO_IMAGE, INTERIOR_IMAGES.living, INTERIOR_IMAGES.kitchen];
const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const ref = useRef(null);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const lines = [
    { text: t.hero.line1, cls: "text-slate-900" },
    { text: t.hero.line2, cls: "text-outline" },
    { text: t.hero.line3, cls: "text-brand" },
  ];

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="grain relative flex min-h-screen items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <RotatingImage images={HERO_SLIDES} interval={6500} alt="TecnoEdil" className="absolute inset-x-0 top-0 h-[115%]" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/20 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-40 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          className="mb-8 text-xs font-semibold uppercase tracking-[0.3em] text-brand"
          data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>

        <h1 className="font-heading text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-headline">
          {lines.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.cls}`}
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: EASE }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
          className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          data-testid="hero-subtitle"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/contatti"
            data-testid="hero-cta-quote"
            className="group flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-brand-amber"
          >
            {t.hero.ctaQuote}
            <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link
            to="/lavori"
            data-testid="hero-cta-projects"
            className="rounded-full border border-slate-300 bg-white/70 px-7 py-3.5 text-sm font-semibold text-slate-800 backdrop-blur transition-[border-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-brand hover:text-brand"
          >
            {t.hero.ctaProjects}
          </Link>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollToSection("#servizi-preview")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-slate-400 transition-colors hover:text-brand sm:flex"
        aria-label={t.hero.scroll}
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown size={18} />
        </motion.span>
      </motion.button>
    </section>
  );
}
